import { api } from '@/lib/api';
import type {
  BacklogItem,
  Board,
  ChatChannel,
  CreatedUserResponse,
  DirectoryUser,
  ItemComment,
  ItemStatus,
  IssuedInvite,
  ItemType,
  Paginated,
  Priority,
  Project,
  RegistrationInvite,
  ProjectDetail,
  ProjectRole,
  Sprint,
  SprintHistoryEntry,
  SprintSummary,
  User,
} from '@/types/api';
import { useAuth } from '@/stores/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/** Claves de cache en un solo sitio, para invalidar sin adivinar. */
export const keys = {
  projects: ['projects'] as const,
  project: (id: string) => ['project', id] as const,
  board: (projectId: string) => ['board', projectId] as const,
  items: (projectId: string) => ['items', projectId] as const,
  item: (id: string) => ['item', id] as const,
  comments: (itemId: string) => ['comments', itemId] as const,
  sprints: (projectId: string) => ['sprints', projectId] as const,
  channels: ['channels'] as const,
  users: (search: string) => ['users', search] as const,
  invites: ['invites'] as const,
  directory: (search: string) => ['directory', search] as const,
  sprintHistory: (search: string, projectId: string) =>
    ['sprint-history', search, projectId] as const,
  sprintSummary: (id: string) => ['sprint-summary', id] as const,
};

// --- Proyectos --------------------------------------------------------------

export function useProjects() {
  return useQuery({
    queryKey: keys.projects,
    queryFn: () => api.get<Project[]>('/projects'),
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string; name: string; description?: string; color?: string }) =>
      api.post<ProjectDetail>('/projects', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.projects });
      // El proyecto nuevo trae su canal de chat, asi que la lista cambia.
      void queryClient.invalidateQueries({ queryKey: keys.channels });
    },
  });
}

export function useAddProjectMember(projectId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; role: ProjectRole }) =>
      api.post<unknown>(`/projects/${projectId}/members`, input),
    onSuccess: () => {
      if (projectId) {
        void queryClient.invalidateQueries({ queryKey: keys.project(projectId) });
      }
      void queryClient.invalidateQueries({ queryKey: keys.projects });
      // Entrar al proyecto es entrar a su canal: la bandeja cambia.
      void queryClient.invalidateQueries({ queryKey: keys.channels });
    },
  });
}

export function useProject(projectId: string | null) {
  return useQuery({
    queryKey: keys.project(projectId ?? ''),
    queryFn: () => api.get<ProjectDetail>(`/projects/${projectId}`),
    enabled: Boolean(projectId),
  });
}

// --- Tablero y backlog ------------------------------------------------------

export function useBoard(projectId: string | null) {
  return useQuery({
    queryKey: keys.board(projectId ?? ''),
    queryFn: () => api.get<Board>(`/projects/${projectId}/board`),
    enabled: Boolean(projectId),
  });
}

export function useBacklogItems(projectId: string | null) {
  return useQuery({
    queryKey: keys.items(projectId ?? ''),
    queryFn: () => api.get<BacklogItem[]>(`/projects/${projectId}/items`),
    enabled: Boolean(projectId),
  });
}

export function useSprints(projectId: string | null) {
  return useQuery({
    queryKey: keys.sprints(projectId ?? ''),
    queryFn: () => api.get<Sprint[]>(`/projects/${projectId}/sprints`),
    enabled: Boolean(projectId),
  });
}

export function useItem(itemId: string | null) {
  return useQuery({
    queryKey: keys.item(itemId ?? ''),
    queryFn: () => api.get<BacklogItem>(`/items/${itemId}`),
    enabled: Boolean(itemId),
  });
}

export function useComments(itemId: string | null) {
  return useQuery({
    queryKey: keys.comments(itemId ?? ''),
    queryFn: () => api.get<ItemComment[]>(`/items/${itemId}/comments`),
    enabled: Boolean(itemId),
  });
}

/**
 * Refresca todo lo que depende del backlog de un proyecto. Mover una tarjeta
 * cambia a la vez el tablero, la lista y las metricas del proyecto, asi que se
 * invalidan juntos en lugar de recordar cual toca en cada mutacion.
 */
function useBacklogInvalidator(projectId: string | null) {
  const queryClient = useQueryClient();
  return () => {
    if (!projectId) {
      return;
    }
    void queryClient.invalidateQueries({ queryKey: keys.board(projectId) });
    void queryClient.invalidateQueries({ queryKey: keys.items(projectId) });
    void queryClient.invalidateQueries({ queryKey: keys.sprints(projectId) });
    void queryClient.invalidateQueries({ queryKey: keys.project(projectId) });
    void queryClient.invalidateQueries({ queryKey: keys.projects });
  };
}

export interface CreateItemInput {
  title: string;
  description?: string;
  type?: ItemType;
  priority?: Priority;
  storyPoints?: number;
  assigneeId?: string;
  sprintId?: string;
  status?: ItemStatus;
  criteria?: string[];
}

export function useCreateItem(projectId: string | null) {
  const invalidate = useBacklogInvalidator(projectId);
  return useMutation({
    mutationFn: (input: CreateItemInput) =>
      api.post<BacklogItem>(`/projects/${projectId}/items`, input),
    onSuccess: invalidate,
  });
}

/**
 * Editar un item. Igual que al arrastrar, el cambio se pinta antes de que el
 * servidor conteste: con la base de datos en la nube, esperar la respuesta hacia
 * parecer que el clic no habia surtido efecto.
 */
export function useUpdateItem(projectId: string | null) {
  const queryClient = useQueryClient();
  const invalidate = useBacklogInvalidator(projectId);

  return useMutation({
    mutationFn: ({ itemId, ...patch }: { itemId: string } & Record<string, unknown>) =>
      api.patch<BacklogItem>(`/items/${itemId}`, patch),

    async onMutate({ itemId, ...patch }) {
      const key = keys.item(itemId);
      await queryClient.cancelQueries({ queryKey: key });

      const previous = queryClient.getQueryData<BacklogItem>(key);
      if (previous) {
        queryClient.setQueryData<BacklogItem>(key, { ...previous, ...patch });
      }
      return { previous, key };
    },

    onError(_error, _input, context) {
      if (context?.previous) {
        queryClient.setQueryData(context.key, context.previous);
      }
    },

    onSuccess(item) {
      queryClient.setQueryData(keys.item(item.id), item);
      invalidate();
    },
  });
}

/**
 * Mover un item entre el backlog y un sprint desde la lista del backlog.
 *
 * Se pinta al instante sobre la lista completa (no sobre el item suelto) para
 * que la fila salte de panel al soltarla, sin el segundo largo de espera que
 * cuesta la ida y vuelta a la base de datos.
 */
export function useAssignSprint(projectId: string | null) {
  const queryClient = useQueryClient();
  const invalidate = useBacklogInvalidator(projectId);
  const listKey = keys.items(projectId ?? '');

  return useMutation({
    mutationFn: ({ itemId, sprintId }: { itemId: string; sprintId: string | null }) =>
      api.patch<BacklogItem>(`/items/${itemId}`, { sprintId }),

    async onMutate({ itemId, sprintId }) {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<BacklogItem[]>(listKey);

      if (previous) {
        queryClient.setQueryData<BacklogItem[]>(
          listKey,
          previous.map((item) =>
            item.id === itemId
              ? {
                  ...item,
                  sprintId,
                  // Misma regla que aplica el servidor: dentro de un sprint el
                  // item deja de estar "sin planificar".
                  status: sprintId
                    ? item.status === 'BACKLOG'
                      ? 'TODO'
                      : item.status
                    : 'BACKLOG',
                }
              : item,
          ),
        );
      }
      return { previous };
    },

    onError(_error, _input, context) {
      if (context?.previous) {
        queryClient.setQueryData(listKey, context.previous);
      }
    },

    onSettled: invalidate,
  });
}

export function useDeleteItem(projectId: string | null) {
  const invalidate = useBacklogInvalidator(projectId);
  return useMutation({
    mutationFn: (itemId: string) => api.delete<void>(`/items/${itemId}`),
    onSuccess: invalidate,
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => api.delete<void>(`/projects/${projectId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.projects });
      void queryClient.invalidateQueries({ queryKey: keys.channels });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.delete<void>(`/users/${userId}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

interface MoveItemInput {
  itemId: string;
  status: ItemStatus;
  afterItemId?: string;
  beforeItemId?: string;
}

/**
 * Mover una tarjeta en el tablero.
 *
 * La tarjeta cambia de columna en el momento, sin esperar al servidor: la
 * base de datos esta en la nube y cada consulta cuesta cientos de
 * milisegundos, asi que esperar la respuesta hacia que arrastrar se sintiera
 * pegajoso. Se pinta el resultado esperado, se manda la peticion y, si algo
 * falla, se devuelve el tablero a como estaba.
 */
export function useMoveItem(projectId: string | null) {
  const queryClient = useQueryClient();
  const boardKey = keys.board(projectId ?? '');

  return useMutation({
    mutationFn: ({ itemId, status, afterItemId, beforeItemId }: MoveItemInput) =>
      api.post<BacklogItem>(`/items/${itemId}/move`, { status, afterItemId, beforeItemId }),

    async onMutate({ itemId, status }) {
      // Un refetch en vuelo pisaria la actualizacion optimista al llegar.
      await queryClient.cancelQueries({ queryKey: boardKey });

      const previous = queryClient.getQueryData<Board>(boardKey);
      if (previous) {
        queryClient.setQueryData<Board>(boardKey, moveItemInBoard(previous, itemId, status));
      }
      return { previous };
    },

    onError(_error, _input, context) {
      if (context?.previous) {
        queryClient.setQueryData(boardKey, context.previous);
      }
    },

    onSettled() {
      // Solo se refrescan el tablero y las metricas del proyecto: el backlog y
      // la lista de sprints no cambian al mover una tarjeta dentro del sprint.
      void queryClient.invalidateQueries({ queryKey: boardKey });
      if (projectId) {
        void queryClient.invalidateQueries({ queryKey: keys.project(projectId) });
      }
    },
  });
}

/** Reubica un item entre columnas sin mutar el tablero original. */
function moveItemInBoard(board: Board, itemId: string, status: ItemStatus): Board {
  const moved = board.columns.flatMap((column) => column.items).find((item) => item.id === itemId);
  if (!moved || moved.status === status) {
    return board;
  }

  const next = { ...moved, status };
  const points = (item: BacklogItem) => item.storyPoints ?? 0;

  return {
    ...board,
    columns: board.columns.map((column) => {
      if (column.status === moved.status) {
        const items = column.items.filter((item) => item.id !== itemId);
        return { ...column, items, count: items.length, storyPoints: items.reduce((a, i) => a + points(i), 0) };
      }
      if (column.status === status) {
        const items = [...column.items, next];
        return { ...column, items, count: items.length, storyPoints: items.reduce((a, i) => a + points(i), 0) };
      }
      return column;
    }),
  };
}

export function useToggleCriterion(itemId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ criterionId, done }: { criterionId: string; done: boolean }) =>
      api.patch<{ id: string; done: boolean }>(`/criteria/${criterionId}`, { done }),
    onSuccess: () => {
      if (itemId) {
        void queryClient.invalidateQueries({ queryKey: keys.item(itemId) });
      }
    },
  });
}

/**
 * Comentar una tarea.
 *
 * El comentario aparece en el acto, marcado como "enviando", y se reemplaza por
 * el que devuelve el servidor. Antes habia que esperar la peticion (1.4 s) mas
 * una recarga de la lista (1.5 s) sin ninguna senal en pantalla, y parecia que
 * el comentario no se habia mandado. La recarga sobraba: la respuesta ya trae
 * el comentario creado.
 */
export function useAddComment(itemId: string | null) {
  const queryClient = useQueryClient();
  const currentUser = useAuth((state) => state.user);
  const listKey = keys.comments(itemId ?? '');

  return useMutation({
    mutationFn: (body: string) => api.post<ItemComment>(`/items/${itemId}/comments`, { body }),

    async onMutate(body) {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<ItemComment[]>(listKey);

      const optimisticId = `pendiente-${Date.now()}`;
      if (currentUser && itemId) {
        queryClient.setQueryData<ItemComment[]>(listKey, [
          ...(previous ?? []),
          {
            id: optimisticId,
            itemId,
            body,
            createdAt: new Date().toISOString(),
            author: {
              id: currentUser.id,
              fullName: currentUser.fullName,
              avatarUrl: currentUser.avatarUrl,
              username: currentUser.username,
            },
            pending: true,
          },
        ]);
      }
      return { previous, optimisticId };
    },

    onError(_error, _body, context) {
      queryClient.setQueryData(listKey, context?.previous ?? []);
    },

    onSuccess(comment, _body, context) {
      // El provisional se cambia por el real, conservando su sitio en la lista.
      queryClient.setQueryData<ItemComment[]>(listKey, (current) =>
        (current ?? []).map((c) => (c.id === context?.optimisticId ? comment : c)),
      );
      if (itemId) {
        // Solo para que el contador de la tarjeta cuadre.
        void queryClient.invalidateQueries({ queryKey: keys.item(itemId) });
      }
    },
  });
}

// --- Sprints ----------------------------------------------------------------

export function useStartSprint(projectId: string | null) {
  const invalidate = useBacklogInvalidator(projectId);
  return useMutation({
    mutationFn: (sprintId: string) => api.post<Sprint>(`/sprints/${sprintId}/start`),
    onSuccess: invalidate,
  });
}

export function useCompleteSprint(projectId: string | null) {
  const invalidate = useBacklogInvalidator(projectId);
  return useMutation({
    mutationFn: ({
      sprintId,
      moveUnfinishedToSprintId,
    }: {
      sprintId: string;
      moveUnfinishedToSprintId?: string;
    }) => api.post<unknown>(`/sprints/${sprintId}/complete`, { moveUnfinishedToSprintId }),
    onSuccess: invalidate,
  });
}

/** Sprints cerrados de los proyectos que ve la persona. */
export function useSprintHistory(search = '', projectId = '') {
  return useQuery({
    queryKey: keys.sprintHistory(search, projectId),
    queryFn: () => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (projectId) params.set('projectId', projectId);
      const query = params.toString();
      return api.get<SprintHistoryEntry[]>(`/sprints/history${query ? `?${query}` : ''}`);
    },
  });
}

export function useSprintSummary(sprintId: string | null) {
  return useQuery({
    queryKey: keys.sprintSummary(sprintId ?? ''),
    queryFn: () => api.get<SprintSummary>(`/sprints/${sprintId}/summary`),
    enabled: Boolean(sprintId),
  });
}

export function useCreateSprint(projectId: string | null) {
  const invalidate = useBacklogInvalidator(projectId);
  return useMutation({
    mutationFn: (input: { name: string; goal?: string; startDate?: string; endDate?: string }) =>
      api.post<Sprint>(`/projects/${projectId}/sprints`, input),
    onSuccess: invalidate,
  });
}

// --- Chat -------------------------------------------------------------------

export function useChannels() {
  return useQuery({
    queryKey: keys.channels,
    queryFn: () => api.get<ChatChannel[]>('/chat/channels'),
  });
}

// --- Usuarios ---------------------------------------------------------------

export function useUsers(search: string) {
  return useQuery({
    queryKey: keys.users(search),
    queryFn: () =>
      api.get<Paginated<User>>(
        `/users?pageSize=50${search ? `&search=${encodeURIComponent(search)}` : ''}`,
      ),
  });
}

/** Personas activas de la plataforma, para los selectores. */
export function useDirectory(search = '') {
  return useQuery({
    queryKey: keys.directory(search),
    queryFn: () =>
      api.get<DirectoryUser[]>(
        `/users/directory${search ? `?search=${encodeURIComponent(search)}` : ''}`,
      ),
    staleTime: 60_000,
  });
}

/**
 * Abre (o recupera) la conversacion uno a uno con alguien.
 *
 * El canal que devuelve el servidor se mete en la lista en el acto en vez de
 * esperar a que se recargue: si no, la conversacion recien abierta tarda unos
 * segundos en aparecer y el panel se queda en blanco mientras tanto.
 */
export function useOpenDirect() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.post<ChatChannel>('/chat/channels/direct', { userId }),
    onSuccess: (channel) => {
      queryClient.setQueryData<ChatChannel[]>(keys.channels, (current) => {
        if (!current) {
          return current;
        }
        return current.some((c) => c.id === channel.id)
          ? current
          : [{ ...channel, unreadCount: 0, lastMessage: null }, ...current];
      });
      void queryClient.invalidateQueries({ queryKey: keys.channels });
    },
  });
}

export function useRemoveProjectMember(projectId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) =>
      api.delete<void>(`/projects/${projectId}/members/${memberId}`),
    onSuccess: () => {
      if (projectId) {
        void queryClient.invalidateQueries({ queryKey: keys.project(projectId) });
      }
      void queryClient.invalidateQueries({ queryKey: keys.projects });
      void queryClient.invalidateQueries({ queryKey: keys.channels });
    },
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      email: string;
      fullName: string;
      jobTitle?: string;
      role: 'ADMIN' | 'MEMBER';
    }) => api.post<CreatedUserResponse>('/users', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

// --- Invitaciones de registro ------------------------------------------------

export function useInvites() {
  return useQuery({
    queryKey: keys.invites,
    queryFn: () => api.get<RegistrationInvite[]>('/auth/invites'),
  });
}

export function useIssueInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (note?: string) => api.post<IssuedInvite>('/auth/invites', { note }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.invites }),
  });
}

export function useRevokeInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) => api.delete<void>(`/auth/invites/${inviteId}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.invites }),
  });
}

export function useResetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.post<CreatedUserResponse>(`/users/${userId}/reset-password`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...patch }: { userId: string } & Record<string, unknown>) =>
      api.patch<User>(`/users/${userId}`, patch),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}
